import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Drawer, Button, Tag, Image, Popover, Spin } from 'antd';
import { isImage } from 'components/Prescriptions/components/AttachFiles';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { FileUploadResult } from 'hooks/useFileUpload';
import { getStandaloneNoteDetail } from 'api/treatment';
import { TStandaloneNoteDetail } from 'types/treatment';
import { TextVisitAttachment } from 'types/textVisit';
import { formatUTCTime } from 'utils/common';
import { sanitizeRichHtml } from 'utils/sanitize';
import { getProviderNameDisplay } from 'utils/provider';
import ProviderStore from 'store/Provider';
import usePrivateFilePreview from 'hooks/usePrivateFilePreview';
import PrintContent from 'components/VideoVisitUnSubmittedDrawer/components/PrintContent';
import { useReactToPrint } from 'react-to-print';
import AddendumsSection from 'components/VideoVisitSubmittedDrawer/AddendumsSection';
import s from './s.module.less';

type Props = {
    show: boolean;
    onClose: () => void;
    noteId?: number;
    specialty?: string;
};

const NonAppointmentSubmittedVisitNoteDrawer = ({ show, onClose, noteId, specialty }: Props) => {
    const [noteDetail, setNoteDetail] = useState<TStandaloneNoteDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState<FileUploadResult[]>([]);
    const printContentRef = useRef<HTMLDivElement>(null);

    const { previewUrl: providerSignaturePreviewUrl } = usePrivateFilePreview(
        noteDetail?.providerSignature?.signatureFileKey,
    );

    const [getUser] = ProviderStore.useStore();
    const providerName = getProviderNameDisplay(getUser('data'));
    const providerCredentials = getUser('data')?.credential;

    const patientName =
        noteDetail?.patientFirstName && noteDetail?.patientLastName
            ? `${noteDetail.patientFirstName} ${noteDetail.patientLastName}`
            : 'Patient';

    const title = specialty || noteDetail?.specialty || 'Visit note';

    const fetchDetail = useCallback(async (id: number) => {
        setLoading(true);
        try {
            const res = await getStandaloneNoteDetail(id);
            if (res && !res.error) {
                setNoteDetail(res.data?.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (show && noteId) {
            fetchDetail(noteId);
        }
        if (!show) {
            setNoteDetail(null);
            setAttachments([]);
        }
    }, [show, noteId, fetchDetail]);

    useEffect(() => {
        const fetchAttachmentUrls = async () => {
            try {
                const resAttachments: FileUploadResult[] = await Promise.all(
                    (noteDetail?.attachments as TextVisitAttachment[])?.map(async (attachment) => {
                        const res = await providerGetKlarityResourcesUrl(attachment.path || '');
                        if (!res.error) {
                            return {
                                path: attachment.path,
                                name: attachment.name,
                                type: attachment.type,
                                previewUrl: res.data?.data?.singedUrl || '',
                            };
                        }
                        return attachment;
                    }),
                );
                setAttachments(resAttachments);
            } catch (error) {
                console.error(error);
            }
        };
        if (noteDetail?.attachments && noteDetail.attachments.length > 0) {
            fetchAttachmentUrls();
        }
    }, [noteDetail?.attachments]);

    const handlePrint = useReactToPrint({
        contentRef: printContentRef,
        documentTitle: 'Visit Notes',
    });

    const renderTreatmentPlan = () => {
        if (!noteDetail) return null;

        return (
            <div className={s.treatmentPlan}>
                <div className={s.section}>
                    <div className={s.sectionTitle}>Response</div>
                    <div className={s.responseContent}>
                        <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(noteDetail.treatmentPlan || '') }} />
                        {attachments?.length > 0 && (
                            <div className={s.attachments}>
                                {attachments.map((attachment, index) => (
                                    <div key={index} className={s.attachmentItem}>
                                        {isImage(attachment) ? (
                                            <div className={s.attachmentPreview}>
                                                <Image
                                                    src={attachment.previewUrl}
                                                    alt={attachment.name}
                                                    preview={{ src: attachment.previewUrl }}
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className={s.fileInfo}
                                                onClick={() => window.open(attachment.previewUrl || '', '_blank')}
                                            >
                                                <div className={s.fileName}>{attachment.name || ''}</div>
                                                <div className={s.fileType}>
                                                    {attachment.name?.split('.').pop()?.toUpperCase() || 'File'}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {noteDetail.providerNote && (
                    <div className={s.section}>
                        <div className={s.sectionTitle}>Private notes</div>
                        <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(noteDetail.providerNote) }} />
                    </div>
                )}

                <div className={s.providerViewOnlyWrap}>
                    <h4>The following session is not visible to patient</h4>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Diagnosis</div>
                        {noteDetail.diagnoses && noteDetail.diagnoses.length > 0 ? (
                            <ul className={s.diagnosisList}>
                                {noteDetail.diagnoses.map((entry, idx) => (
                                    <li key={idx}>
                                        {entry.diagnosisCode
                                            ? `${entry.diagnosisCode} - ${entry.description || ''}`
                                            : entry.description || '-'}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div>-</div>
                        )}
                    </div>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Billing codes</div>
                        {noteDetail.billingCodes && noteDetail.billingCodes.length > 0 ? (
                            <div className={s.billingCodesList}>
                                <ul className={s.billingCodesUl}>
                                    {noteDetail.billingCodes.map((entry, idx) => (
                                        <li key={idx}>
                                            <div className={s.billingCodeRow}>
                                                <span className={s.billingCodeName}>
                                                    {entry.cptCode || '-'} — {entry.description || '-'}
                                                </span>
                                                <span className={s.billingCodeTag}>
                                                    <Tag className={entry.isPrimary ? s.primaryTag : s.addOnTag}>
                                                        {entry.isPrimary ? 'Primary' : 'Add-on'}
                                                    </Tag>
                                                </span>
                                                <span className={s.billingCodeUnit}>
                                                    {{
                                                        TIMED: 'Timed',
                                                        PER_ITEM_ADMINISTRATION: 'Per item/administration',
                                                        PER_SESSION: 'Per session',
                                                    }[entry.unitBasis || 'PER_SESSION'] || entry.unitBasis}
                                                </span>
                                                <span className={s.billingCodeUnit}>
                                                    {entry.units || 1} Unit{(entry.units || 1) > 1 ? 's' : ''}
                                                </span>
                                                <span className={s.billingCodeRate}>
                                                    {entry.insuranceRate != null
                                                        ? `$${entry.insuranceRate.toFixed(2)}`
                                                        : '-'}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className={s.claimTotal}>
                                    Claim total{' '}
                                    <strong>
                                        $
                                        {noteDetail.billingCodes
                                            .reduce(
                                                (sum, e) => sum + (e.insuranceRate || 0) * (e.units || 1),
                                                0,
                                            )
                                            .toFixed(2)}
                                    </strong>
                                </div>
                            </div>
                        ) : (
                            <div>-</div>
                        )}
                    </div>
                </div>

                <div className={s.section}>
                    <div className={s.sectionTitle}>Signatures</div>
                    <div className="flex gap-1 flex-col">
                        {providerSignaturePreviewUrl && (
                            <img src={providerSignaturePreviewUrl} alt="Provider signature" />
                        )}
                        <div>
                            Signed by {providerName}, {providerCredentials}
                        </div>
                        <div>{formatUTCTime(noteDetail.treatmentSubmittedAt || '', 'MM/DD/YYYY, hh:mm A')}</div>
                    </div>
                </div>

                {noteId && (
                    <AddendumsSection treatmentId={noteId} businessId={noteId} />
                )}
            </div>
        );
    };

    return (
        <>
            <Drawer
                open={show}
                onClose={onClose}
                title={
                    <div className={s.header}>
                        <span className="text-base font-medium">{title}</span>
                        <div className="flex items-center gap-2">
                            <Popover
                                content="Printing only includes the text of the visit notes. Attachments must be downloaded individually."
                                overlayStyle={{ width: 300 }}
                            >
                                <Button type="text" style={{ padding: 0 }} onClick={handlePrint}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M17 17H19C20.1046 17 21 16.1046 21 15V11C21 9.89543 20.1046 9 19 9H5C3.89543 9 3 9.89543 3 11V15C3 16.1046 3.89543 17 5 17H7M9 21H15C16.1046 21 17 20.1046 17 19V15C17 13.8954 16.1046 13 15 13H9C7.89543 13 7 13.8954 7 15V19C7 20.1046 7.89543 21 9 21ZM17 9V5C17 3.89543 16.1046 3 15 3H9C7.89543 3 7 3.89543 7 5V9H17Z" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Button>
                            </Popover>
                            <Button type="text" onClick={onClose} style={{ padding: 0 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M6 18L18 6M6 6L18 18" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Button>
                        </div>
                    </div>
                }
                closeIcon={null}
                rootClassName={s.drawer}
                width={900}
            >
                <Spin spinning={loading}>
                    {noteDetail && (
                        <div className={s.contentInfo}>
                            <div className={s.title}>[{patientName}] {noteDetail.specialty || 'Visit note'}</div>
                            <div className={s.timeTable}>
                                <div className={s.tableRow}>
                                    <div className={s.tableCell}>
                                        <div className={s.label}>Sent to patient on</div>
                                        <div className={s.value}>
                                            {formatUTCTime(noteDetail.treatmentSubmittedAt || '', 'MM/DD/YYYY, hh:mm A')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {renderTreatmentPlan()}
                        </div>
                    )}
                </Spin>
            </Drawer>
            <div className={s.printWrapper}>
                <PrintContent
                    ref={printContentRef}
                    data={{ treatmentPlan: noteDetail?.treatmentPlan || '', attachments: noteDetail?.attachments } as any}
                    caseData={{
                        patientFirstName: noteDetail?.patientFirstName,
                        patientLastName: noteDetail?.patientLastName,
                        patientBirthday: '',
                        startAt: noteDetail?.treatmentSubmittedAt || '',
                        serviceType: noteDetail?.specialty || 'Visit note',
                    } as any}
                />
            </div>
        </>
    );
};

export default NonAppointmentSubmittedVisitNoteDrawer;
